import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X, Mail, CheckCircle2 } from "lucide-react";

/**
 * ContactSalesDialog — public enterprise-inquiry form.
 * Submits to POST /api/contact/enterprise which stores the lead in
 * `enterprise_leads` and emails mmacias2184@gmail.com via Resend.
 *
 * Props:
 *   open       boolean — dialog visibility
 *   onClose    close handler
 *   context    optional string prepended to the message field
 *                (e.g. "Requesting Enterprise plan pricing")
 */
export default function ContactSalesDialog({ open, onClose, context }) {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", team_size: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        message: [context, form.message].filter(Boolean).join("\n\n"),
      };
      await api.post("/contact/enterprise", payload);
      setDone(true);
      toast.success("Message sent — we'll be in touch shortly");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed — try again or email hello@bidforgeai.org");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setDone(false);
    setForm({ name: "", company: "", email: "", phone: "", team_size: "", message: "" });
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      data-testid="contact-sales-dialog"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden isolate" style={{ backgroundColor: "#ffffff" }}>
        <div className="px-6 pt-5 pb-4 border-b border-slate-200 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
            <Mail className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-mono font-bold tracking-widest text-amber-600">CONTACT SALES · ENTERPRISE</div>
            <div className="text-lg font-display font-bold text-slate-900 leading-tight mt-0.5">Talk to us about Enterprise</div>
          </div>
          <button onClick={close} aria-label="Close" className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="contact-sales-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="contact-sales-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <div className="text-xl font-display font-bold text-slate-900 mb-2">Message sent</div>
            <div className="text-sm text-slate-600 mb-6">We'll reach out within one business day. In the meantime, keep an eye on your inbox — a copy of your submission is on its way.</div>
            <button onClick={close} className="px-5 py-2.5 rounded-full text-xs font-mono font-bold tracking-widest bg-slate-900 text-white hover:bg-slate-800" data-testid="contact-sales-close-success">DONE</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-mono font-bold tracking-widest text-slate-500 block mb-1.5">NAME *</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white border border-slate-200 focus:border-violet-400 px-3 py-2.5 text-sm rounded-lg outline-none" data-testid="contact-sales-name" />
              </div>
              <div>
                <label className="text-[10px] font-mono font-bold tracking-widest text-slate-500 block mb-1.5">COMPANY *</label>
                <input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full bg-white border border-slate-200 focus:border-violet-400 px-3 py-2.5 text-sm rounded-lg outline-none" data-testid="contact-sales-company" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-mono font-bold tracking-widest text-slate-500 block mb-1.5">EMAIL *</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-white border border-slate-200 focus:border-violet-400 px-3 py-2.5 text-sm rounded-lg outline-none" data-testid="contact-sales-email" />
              </div>
              <div>
                <label className="text-[10px] font-mono font-bold tracking-widest text-slate-500 block mb-1.5">PHONE</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-white border border-slate-200 focus:border-violet-400 px-3 py-2.5 text-sm rounded-lg outline-none" data-testid="contact-sales-phone" />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono font-bold tracking-widest text-slate-500 block mb-1.5">TEAM SIZE</label>
              <select value={form.team_size} onChange={(e) => setForm({ ...form, team_size: e.target.value })} className="w-full bg-white border border-slate-200 focus:border-violet-400 px-3 py-2.5 text-sm rounded-lg outline-none" data-testid="contact-sales-teamsize">
                <option value="">Select team size…</option>
                <option value="1-10">1-10</option>
                <option value="11-50">11-50</option>
                <option value="51-200">51-200</option>
                <option value="201-1000">201-1000</option>
                <option value="1000+">1000+</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono font-bold tracking-widest text-slate-500 block mb-1.5">MESSAGE</label>
              <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your BD workflow, current tools, and what an ideal partnership looks like." className="w-full bg-white border border-slate-200 focus:border-violet-400 px-3 py-2.5 text-sm rounded-lg outline-none resize-none" data-testid="contact-sales-message" />
            </div>
            <div className="flex items-center justify-between gap-3 pt-2">
              <button type="button" onClick={close} className="text-sm font-mono font-bold tracking-widest text-slate-600 hover:text-slate-900" data-testid="contact-sales-cancel">CANCEL</button>
              <button
                type="submit"
                disabled={busy}
                className={`px-5 py-2.5 rounded-full text-xs font-mono font-bold tracking-widest text-white transition-all flex items-center gap-2 ${busy ? "bg-slate-400 cursor-not-allowed" : "bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 shadow-md"}`}
                data-testid="contact-sales-submit"
              >
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" />SENDING…</> : "SEND MESSAGE"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
